import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiConfig from '../config/api.js';

const useWebSocket = (chatId = null) => {
  const { user } = useAuth();
  const [ws, setWs] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Extract userId to prevent unnecessary re-renders
  const userId = user?.id;
  const userName = user?.firstName || user?.email;

  // WebSocket connection
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      // Removed excessive logging - only log actual connection attempts, not skips
      return;
    }

    try {
      const websocket = new WebSocket(apiConfig.websocketUrl);
      wsRef.current = websocket;

      websocket.onopen = () => {
        setIsConnected(true);
        setError(null);
        
        // Send user online status with a small delay to ensure connection is fully established
        if (userId && userName) {
          setTimeout(() => {
            if (websocket.readyState === WebSocket.OPEN) {
              websocket.send(JSON.stringify({
                type: 'user_online',
                userId: userId,
                userName: userName
              }));
            }
          }, 100);
        }
      };

      websocket.onmessage = (event) => {
        try {
          let messageData = event.data;
          
          // Handle Echo messages from API Gateway WebSocket
          if (messageData.startsWith('Echo: ')) {
            messageData = messageData.substring(6); // Remove "Echo: " prefix
          }
          
          const data = JSON.parse(messageData);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error, 'Raw data:', event.data);
        }
      };

      websocket.onclose = (event) => {
        setIsConnected(false);
        
        // Clear any existing reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        
        // Only attempt to reconnect if it wasn't a manual close (code 1000)
        if (event.code !== 1000) {
          console.log('🔄 Scheduling WebSocket reconnection in 3 seconds...');
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 3000);
        }
      };

      websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('WebSocket connection error');
        setIsConnected(false);
      };

      setWs(websocket);
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setError('Failed to connect to WebSocket');
    }
  }, [userId, userName]); // Only depend on userId and userName, not entire user object

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'connected':
        console.log('WebSocket server connected');
        break;
        
      case 'new_message':
        setMessages(prev => [...prev, data.message]);
        break;
        
      case 'typing_start':
        setTypingUsers(prev => {
          const newTypingUsers = prev.filter(u => u.userId !== data.userId);
          return [...newTypingUsers, { userId: data.userId, userName: data.userName }];
        });
        break;
        
      case 'typing_stop':
        setTypingUsers(prev => prev.filter(u => u.userId !== data.userId));
        break;
        
      case 'user_joined':
        setActiveUsers(prev => {
          if (!prev.find(u => u.userId === data.userId)) {
            return [...prev, { userId: data.userId, status: 'online' }];
          }
          return prev;
        });
        break;
        
      case 'user_left':
      case 'user_disconnected':
        setActiveUsers(prev => prev.filter(u => u.userId !== data.userId));
        setTypingUsers(prev => prev.filter(u => u.userId !== data.userId));
        break;
        
      case 'user_offline':
        setActiveUsers(prev => prev.filter(u => u.userId !== data.userId));
        break;
        
      case 'dashboard_update':
        // Handle dashboard updates
        if (window.dashboardUpdateHandler) {
          window.dashboardUpdateHandler(data.data);
        }
        break;
        
      case 'notification':
        // Handle notifications
        if (window.notificationHandler) {
          window.notificationHandler(data.data);
        }
        break;
        
      case 'dashboard_subscribed':
        console.log('Subscribed to dashboard updates');
        break;
        
      case 'dashboard_unsubscribed':
        console.log('Unsubscribed from dashboard updates');
        break;
        
      case 'notifications_subscribed':
        console.log('Subscribed to notifications');
        break;
        
      case 'notifications_unsubscribed':
        console.log('Unsubscribed from notifications');
        break;
        
      case 'user_online':
        console.log('User online:', data.userName);
        break;
        
      case 'subscribe_notifications':
        console.log('Subscribe notifications request processed');
        break;
        
      case 'subscribe_dashboard':
        console.log('Subscribe dashboard request processed');
        break;
        
      default:
        console.log('Unknown WebSocket message type:', data.type);
    }
  }, []);

  // Join chat room
  const joinChat = useCallback((chatId) => {
    if (ws && isConnected && userId) {
      ws.send(JSON.stringify({
        type: 'join_chat',
        userId: userId,
        chatId: chatId
      }));
    }
  }, [ws, isConnected, userId]);

  // Leave chat room
  const leaveChat = useCallback((chatId) => {
    if (ws && isConnected && userId) {
      ws.send(JSON.stringify({
        type: 'leave_chat',
        userId: userId,
        chatId: chatId
      }));
    }
  }, [ws, isConnected, userId]);

  // Send typing start
  const sendTypingStart = useCallback((chatId) => {
    if (ws && isConnected && userId && userName) {
      ws.send(JSON.stringify({
        type: 'typing_start',
        userId: userId,
        chatId: chatId,
        userName: userName
      }));
    }
  }, [ws, isConnected, userId, userName]);

  // Send typing stop
  const sendTypingStop = useCallback((chatId) => {
    if (ws && isConnected && userId) {
      ws.send(JSON.stringify({
        type: 'typing_stop',
        userId: userId,
        chatId: chatId
      }));
    }
  }, [ws, isConnected, userId]);

  // Send new message
  const sendMessage = useCallback((chatId, messageData) => {
    console.log('🔍 WebSocket sendMessage debug:', { 
      ws: !!ws, 
      isConnected, 
      chatId, 
      messageData,
      wsReadyState: ws?.readyState
    });
    
    if (ws && isConnected && ws.readyState === WebSocket.OPEN) {
      try {
        const message = JSON.stringify({
          type: 'new_message',
          chatId: chatId,
          messageData: messageData
        });
        console.log('📤 Sending WebSocket message:', message);
        ws.send(message);
      } catch (error) {
        console.error('❌ WebSocket send error:', error);
      }
    } else {
      console.log('❌ WebSocket not ready for sending:', { 
        ws: !!ws, 
        isConnected, 
        readyState: ws?.readyState 
      });
    }
  }, [ws, isConnected]);

  // Handle typing with timeout
  const handleTyping = useCallback((chatId) => {
    sendTypingStart(chatId);
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set new timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingStop(chatId);
    }, 1000);
  }, [sendTypingStart, sendTypingStop]);

  // Stop typing
  const stopTyping = useCallback((chatId) => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    sendTypingStop(chatId);
  }, [sendTypingStop]);

  // Track if we've attempted connection to prevent repeated attempts
  const hasAttemptedConnection = useRef(false);
  
  // Initialize connection - only when userId changes, not user object
  useEffect(() => {
    if (userId && !hasAttemptedConnection.current) {
      hasAttemptedConnection.current = true;
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, [userId, connect]); // Only depend on userId, not entire user object
  
  // Reset connection attempt flag when userId changes
  useEffect(() => {
    hasAttemptedConnection.current = false;
  }, [userId]);

  // Join/leave chat when chatId changes
  useEffect(() => {
    if (chatId && isConnected) {
      joinChat(chatId);
    }

    return () => {
      if (chatId && isConnected) {
        leaveChat(chatId);
      }
    };
  }, [chatId, isConnected, joinChat, leaveChat]);

  // Subscribe to dashboard updates
  const subscribeToDashboard = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && userId) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe_dashboard',
        userId: userId
      }));
    }
  }, [userId]);

  // Unsubscribe from dashboard updates
  const unsubscribeFromDashboard = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && userId) {
      wsRef.current.send(JSON.stringify({
        type: 'unsubscribe_dashboard',
        userId: userId
      }));
    }
  }, [userId]);

  // Subscribe to notifications
  const subscribeToNotifications = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && userId) {
      try {
        wsRef.current.send(JSON.stringify({
          type: 'subscribe_notifications',
          userId: userId
        }));
      } catch (error) {
        console.error('Error subscribing to notifications:', error);
      }
    }
  }, [userId]);

  // Unsubscribe from notifications
  const unsubscribeFromNotifications = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && userId) {
      try {
        wsRef.current.send(JSON.stringify({
          type: 'unsubscribe_notifications',
          userId: userId
        }));
      } catch (error) {
        console.error('Error unsubscribing from notifications:', error);
      }
    }
  }, [userId]);

  return {
    isConnected,
    typingUsers,
    activeUsers,
    messages,
    error,
    joinChat,
    leaveChat,
    sendMessage,
    handleTyping,
    stopTyping,
    subscribeToDashboard,
    unsubscribeFromDashboard,
    subscribeToNotifications,
    unsubscribeFromNotifications,
    reconnect: connect
  };
};

export default useWebSocket;
